import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Card, HueLogoTile, Input, PasswordStrengthInput } from '@hue/design-system';
import {
  Building2,
  ChevronRight,
  Eye,
  EyeOff,
  KeyRound,
  LockKeyhole,
  Mail,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { getApiError, getApiErrorCode, rootApi, userApi, type UserMfaResult } from '../lib/api.js';
import { SessionNotEstablishedError } from '../lib/session.js';
import { contextKey, contextSubtitle, contextTitle } from '../lib/session-contexts.js';
import { AuthenticatorHelp } from './authenticator-help.js';
import { MfaSetup } from './mfa-qr.js';
import { PortalMasthead } from './portal-masthead.js';
import { ThemeToggle } from './theme-toggle.js';

const credentialsSchema = z.object({
  email: z.email('Informe um e-mail válido.'),
  password: z.string().min(1, 'Informe a senha.'),
});
const mfaSchema = z.object({
  code: z.string().regex(/^\d{6}$/, 'Informe os 6 dígitos.'),
  newPassword: z.string().optional(),
});
/// O primeiro acesso cobra a senha nova com a mesma régua da ativação por convite.
const firstAccessSchema = mfaSchema.extend({
  newPassword: z
    .string()
    .min(8, 'Use ao menos 8 caracteres.')
    .regex(/[A-Z]/, 'Inclua uma letra maiúscula.')
    .regex(/\d/, 'Inclua um número.')
    .regex(/[^A-Za-z0-9]/, 'Inclua um símbolo.'),
});

type Credentials = z.infer<typeof credentialsSchema>;
type MfaCode = z.infer<typeof mfaSchema>;
/// O que falta para entrar de quem foi criado com senha inicial: parear o autenticador, trocar a
/// senha, ou os dois (ADR 0032).
type FirstAccess = {
  mfaUri: string | null;
  requiresPasswordChange: boolean;
  mfaRequired?: boolean | undefined;
};
export type LoginMode = 'user' | 'root';

function loginError(error: unknown): string {
  return error instanceof SessionNotEstablishedError ? error.message : getApiError(error);
}

export function RootLogin({
  onAuthenticated,
  onForgotPassword,
  onSignup,
  initialMode = 'user',
}: {
  /// Recebe o contexto escolhido: a rota decide para onde levar quem entrou como cliente.
  onAuthenticated: (chosen?: 'CUSTOMER' | 'STAFF') => Promise<void>;
  onForgotPassword: (mode: LoginMode) => void;
  /// Quem ainda não tem conta vai direto ao cadastro (ADR 0067).
  onSignup?: (() => void) | undefined;
  initialMode?: LoginMode;
}) {
  const [mode, setMode] = useState<LoginMode>(initialMode);
  const [challengeToken, setChallengeToken] = useState<string>();
  const [contextSelection, setContextSelection] = useState<{
    selectionToken: string;
    contexts: Extract<UserMfaResult, { status: 'CONTEXT_SELECTION_REQUIRED' }>['contexts'];
  }>();
  const [firstAccess, setFirstAccess] = useState<FirstAccess>();
  const [requestError, setRequestError] = useState<string>();
  /// Enquanto uma escolha de contexto está a caminho, as outras ficam travadas: o ticket serve uma
  /// vez só, e o segundo clique só traria "Seleção expirada" por cima da entrada que deu certo.
  const [selectingContext, setSelectingContext] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const credentials = useForm<Credentials>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: '', password: '' },
  });
  const requiresPasswordChange = firstAccess?.requiresPasswordChange ?? false;
  const mfa = useForm<MfaCode>({
    resolver: (values, context, options) =>
      zodResolver(requiresPasswordChange ? firstAccessSchema : mfaSchema)(
        firstAccess?.mfaRequired === false ? { ...values, code: '000000' } : values,
        context,
        options,
      ),
    defaultValues: { code: '', newPassword: '' },
  });
  const newPasswordValue = useWatch({ control: mfa.control, name: 'newPassword' });

  async function submitCredentials(data: Credentials) {
    setRequestError(undefined);
    try {
      if (mode === 'root') {
        const token = await rootApi.login(data);
        if (!token) {
          await onAuthenticated('STAFF');
          return;
        }
        setChallengeToken(token);
        return;
      }
      const result = await userApi.login(data);
      // Quem só é cliente e não ligou o código já entrou com a senha (ADR 0067).
      if (result.status === 'AUTHENTICATED') {
        await onAuthenticated('CUSTOMER');
        return;
      }
      if (result.status === 'CONTEXT_SELECTION_REQUIRED') {
        setChallengeToken('context-selection');
        setContextSelection(result);
        return;
      }
      setChallengeToken(result.challengeToken);
      if (result.status === 'FIRST_ACCESS_REQUIRED') {
        setFirstAccess({
          mfaUri: result.mfaUri,
          mfaRequired: result.mfaRequired,
          requiresPasswordChange: result.requiresPasswordChange,
        });
      }
    } catch (error) {
      setRequestError(loginError(error));
    }
  }

  async function submitMfa(data: MfaCode) {
    if (!challengeToken) return;
    setRequestError(undefined);
    try {
      if (mode === 'root') {
        await rootApi.completeMfa({ challengeToken, code: data.code });
        await onAuthenticated();
        return;
      }
      const result = await userApi.completeMfa({
        challengeToken,
        code: data.code,
        ...(requiresPasswordChange ? { newPassword: data.newPassword } : {}),
      });
      if (result.status === 'CONTEXT_SELECTION_REQUIRED') {
        setContextSelection({
          selectionToken: result.selectionToken,
          contexts: result.contexts,
        });
        return;
      }
      await onAuthenticated('CUSTOMER');
    } catch (error) {
      setRequestError(loginError(error));
    }
  }

  async function selectContext(
    context: Extract<UserMfaResult, { status: 'CONTEXT_SELECTION_REQUIRED' }>['contexts'][number],
  ) {
    if (!contextSelection || selectingContext) return;
    setSelectingContext(true);
    setRequestError(undefined);
    try {
      await userApi.selectContext(
        context.kind === 'TENANT_MEMBER'
          ? {
              selectionToken: contextSelection.selectionToken,
              kind: 'TENANT_MEMBER',
              tenantId: context.tenantId,
            }
          : { selectionToken: contextSelection.selectionToken, kind: context.kind },
      );
    } catch (error) {
      setSelectingContext(false);
      // Ticket vencido ou já usado não volta a servir: a escolha só recomeça pela senha.
      if (getApiErrorCode(error) === 'AUTH_CHALLENGE_INVALID') restart();
      setRequestError(loginError(error));
      return;
    }
    // O ticket foi gasto pela escolha que deu certo. Se a sessão não se confirmar, a tela volta à
    // senha com o motivo, em vez de oferecer de novo um ticket que o servidor já encerrou.
    try {
      await onAuthenticated(context.kind === 'CUSTOMER' ? 'CUSTOMER' : 'STAFF');
    } catch (error) {
      restart();
      setRequestError(loginError(error));
    } finally {
      setSelectingContext(false);
    }
  }

  function restart() {
    setChallengeToken(undefined);
    setFirstAccess(undefined);
    setContextSelection(undefined);
    credentials.resetField('password');
    mfa.reset();
  }

  function changeMode(nextMode: 'user' | 'root') {
    setMode(nextMode);
    setChallengeToken(undefined);
    setFirstAccess(undefined);
    setContextSelection(undefined);
    setRequestError(undefined);
    credentials.reset();
    mfa.reset();
  }

  return (
    <>
      {mode === 'user' ? <PortalMasthead showGuestActions={false} /> : null}
      <div
        className={`relative grid place-items-center overflow-hidden bg-surface px-4 py-10 ${mode === 'user' ? 'min-h-[calc(100dvh-78px)]' : 'min-h-screen'}`}
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-1/2 top-12 h-64 w-64 -translate-x-1/2 rounded-full bg-brand-500/[.07] blur-3xl" />
        </div>
        {mode === 'root' ? (
          <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
            <ThemeToggle />
          </div>
        ) : null}

        <div className="relative w-full max-w-md">
          <div className="mb-8 text-center">
            <HueLogoTile className="mx-auto mb-4 size-16 rounded-2xl" />
            <h1 className="text-2xl font-bold tracking-[-.035em]">
              HUE<span className="text-brand-400">•</span>System
            </h1>
            <p className="mt-1.5 text-sm text-content-muted">
              {mode === 'root' ? 'Console administrativo' : 'Agendamentos e gestão empresarial'}
            </p>
          </div>

          <Card className="border-surface-border bg-surface-card p-6 sm:p-7">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold">
                  {contextSelection
                    ? 'Como você quer entrar?'
                    : firstAccess
                      ? firstAccess.requiresPasswordChange
                        ? 'Primeiro acesso'
                        : 'Cadastre seu autenticador'
                      : challengeToken
                        ? 'Confirme o MFA'
                        : mode === 'root'
                          ? 'Acesso da plataforma'
                          : 'Entrar'}
                </h2>
                <p className="mt-1.5 text-sm leading-5 text-content-muted">
                  {contextSelection
                    ? 'Você pode trocar depois, sem sair. Cada opção abre só os poderes dela.'
                    : firstAccess
                      ? !firstAccess.requiresPasswordChange
                        ? 'Seu autenticador foi redefinido. Cadastre o aplicativo para continuar; sua senha não mudou.'
                        : firstAccess.mfaUri
                          ? 'Vincule seu aplicativo autenticador e escolha a senha que será só sua.'
                          : 'Escolha a senha que será só sua para concluir o primeiro acesso.'
                      : challengeToken
                        ? 'Digite o código atual do seu aplicativo autenticador.'
                        : mode === 'root'
                          ? 'Entre no console global da plataforma.'
                          : 'Com o e-mail e a senha da sua conta.'}
                </p>
              </div>
              {challengeToken && !contextSelection ? (
                <AuthenticatorHelp />
              ) : (
                <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-400">
                  <ShieldCheck className="size-5" />
                </span>
              )}
            </div>

            {requestError ? (
              <div
                role="alert"
                className="mb-5 rounded-xl border border-danger/35 bg-danger/10 px-4 py-3 text-sm text-danger"
              >
                {requestError}
              </div>
            ) : null}

            {!challengeToken ? (
              <form
                key="credentials"
                noValidate
                className="space-y-4"
                onSubmit={(event) => void credentials.handleSubmit(submitCredentials)(event)}
              >
                <Input
                  label={mode === 'root' ? 'E-mail do root' : 'E-mail'}
                  type="email"
                  placeholder="voce@exemplo.com"
                  autoComplete="username"
                  autoFocus
                  leftIcon={<Mail className="size-4" />}
                  error={credentials.formState.errors.email?.message}
                  {...credentials.register('email')}
                />
                <Input
                  label="Senha"
                  type={passwordVisible ? 'text' : 'password'}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  leftIcon={<LockKeyhole className="size-4" />}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setPasswordVisible((visible) => !visible)}
                      aria-label={passwordVisible ? 'Ocultar senha' : 'Mostrar senha'}
                      className="grid size-10 place-items-center rounded-lg transition hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                    >
                      {passwordVisible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </button>
                  }
                  error={credentials.formState.errors.password?.message}
                  {...credentials.register('password')}
                />
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => onForgotPassword(mode)}
                    className="min-h-10 rounded-lg px-2 py-2 text-xs font-semibold text-brand-400 transition hover:text-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                  >
                    Esqueci minha senha
                  </button>
                </div>
                <Button
                  className="mt-2 w-full"
                  size="lg"
                  loading={credentials.formState.isSubmitting}
                >
                  Continuar
                </Button>
              </form>
            ) : contextSelection ? (
              <div key="context-selection" className="space-y-3">
                {contextSelection.contexts.map((context, index) => (
                  <div key={contextKey(context)} className="space-y-3">
                    {context.kind !== 'CUSTOMER' &&
                    contextSelection.contexts[index - 1]?.kind === 'CUSTOMER' ? (
                      <p className="pt-2 text-[11px] font-semibold uppercase tracking-[.12em] text-content-faint">
                        Onde você trabalha
                      </p>
                    ) : null}
                    <button
                      type="button"
                      disabled={selectingContext}
                      aria-busy={selectingContext}
                      onClick={() => void selectContext(context)}
                      className="group flex w-full cursor-pointer items-center gap-3 rounded-xl border border-surface-border bg-surface-elevated px-4 py-3 text-left transition hover:border-brand-500/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 disabled:cursor-wait disabled:opacity-60"
                    >
                      <span className="grid size-9 place-items-center rounded-lg bg-brand-500/10 text-brand-400">
                        {context.kind === 'PLATFORM' ? (
                          <ShieldCheck className="size-4" />
                        ) : context.kind === 'CUSTOMER' ? (
                          <UserRound className="size-4" />
                        ) : (
                          <Building2 className="size-4" />
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <strong className="block truncate text-sm">{contextTitle(context)}</strong>
                        <span className="block truncate text-xs text-content-faint">
                          {contextSubtitle(context)}
                        </span>
                      </span>
                      <ChevronRight className="size-4 text-content-faint transition group-hover:translate-x-0.5 group-hover:text-brand-400" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <form
                key="mfa"
                noValidate
                className="space-y-5"
                onSubmit={(event) => void mfa.handleSubmit(submitMfa)(event)}
              >
                {firstAccess?.mfaUri ? (
                  <MfaSetup uri={firstAccess.mfaUri} qrClassName="mx-auto w-44" />
                ) : null}
                {firstAccess?.mfaRequired === false ? null : (
                  <Input
                    label="Código de autenticação"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={6}
                    placeholder="000000"
                    leftIcon={<KeyRound className="size-4" />}
                    error={mfa.formState.errors.code?.message}
                    {...mfa.register('code')}
                  />
                )}
                {requiresPasswordChange ? (
                  <PasswordStrengthInput
                    label="Crie sua senha"
                    autoComplete="new-password"
                    name="newPassword"
                    onBlur={() => void mfa.trigger('newPassword')}
                    value={newPasswordValue ?? ''}
                    onValueChange={(value) =>
                      mfa.setValue('newPassword', value, {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      })
                    }
                    error={mfa.formState.errors.newPassword?.message}
                  />
                ) : null}
                <Button className="w-full" size="lg" loading={mfa.formState.isSubmitting}>
                  {firstAccess
                    ? firstAccess.requiresPasswordChange
                      ? 'Concluir primeiro acesso'
                      : 'Concluir cadastro'
                    : mode === 'root'
                      ? 'Entrar no console'
                      : 'Entrar'}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setChallengeToken(undefined);
                    setFirstAccess(undefined);
                    setRequestError(undefined);
                    mfa.reset();
                  }}
                  className="min-h-10 w-full rounded-lg px-2 py-2 text-sm text-content-secondary transition hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
                >
                  Voltar ao e-mail e senha
                </button>
              </form>
            )}

            {!challengeToken && mode === 'user' && onSignup ? (
              <p className="mt-5 text-center text-sm text-content-muted">
                Ainda não tem conta?{' '}
                <button
                  type="button"
                  onClick={onSignup}
                  className="font-semibold text-brand-500 underline underline-offset-2 transition hover:text-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                >
                  Cadastre-se aqui
                </button>
              </p>
            ) : null}
            {!challengeToken ? (
              <div className="mt-5 border-t border-surface-border pt-4 text-center">
                <button
                  type="button"
                  onClick={() => changeMode(mode === 'root' ? 'user' : 'root')}
                  className="min-h-10 rounded-lg px-3 py-2 text-xs font-semibold text-content-muted transition hover:text-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                >
                  {mode === 'root' ? 'Voltar ao acesso da empresa' : 'Acesso da plataforma'}
                </button>
              </div>
            ) : null}
          </Card>
          <p className="mt-5 text-center text-xs text-content-faint">
            {mode === 'root'
              ? 'Acesso auditado · console global'
              : 'Acesso protegido · cada empresa vê só o que é dela'}
          </p>
        </div>
      </div>
    </>
  );
}
